import { useCallback } from 'react';
import { fidoneConfigService } from '../../services/fidoneConfigService';
import type {
  Enseigne,
  Client,
  CreateEnseigneRequest,
  CreateClientRequest,
  UpdateClientRequest,
  ClientPreferences,
} from '../../types/preferences';

export const useClientPreferences = () => {
  const getEnseigne = useCallback(
    async (code: string): Promise<Enseigne | null> => {
      try {
        return await fidoneConfigService.getEnseigne(code);
      } catch (error) {
        console.error(`Enseigne ${code} non trouvée:`, error);
        return null;
      }
    },
    []
  );

  const createEnseigne = useCallback(
    async (data: CreateEnseigneRequest): Promise<Enseigne> => {
      return await fidoneConfigService.createEnseigne(data);
    },
    []
  );

  const getOrCreateEnseigne = useCallback(
    async (code: string, displayName: string): Promise<Enseigne> => {
      const existing = await getEnseigne(code);
      return existing || createEnseigne({ code, displayName });
    },
    [getEnseigne, createEnseigne]
  );

  const getClient = useCallback(
    async (enseigneCode: string, clientId: string): Promise<Client | null> => {
      try {
        const client = await fidoneConfigService.getClient(enseigneCode, clientId);
        return { ...client, clientId };
      } catch (error) {
        console.error(`Client ${clientId} non trouvé:`, error);
        return null;
      }
    },
    []
  );

  const createClient = useCallback(
    async (enseigneCode: string, data: CreateClientRequest): Promise<Client> => {
      return await fidoneConfigService.createClient(enseigneCode, data);
    },
    []
  );

  const updateClient = useCallback(
    async (
      enseigneCode: string,
      clientId: string,
      data: UpdateClientRequest
    ): Promise<Client> => {
      const client = await fidoneConfigService.updateClient(enseigneCode, clientId, data);
      return { ...client, clientId };
    },
    []
  );

  const getOrCreateClient = useCallback(
    async (
      enseigneCode: string,
      clientId: string,
      displayName: string,
      initialPreferences?: ClientPreferences
    ): Promise<Client> => {
      const existing = await getClient(enseigneCode, clientId);
      if (existing) return existing;

      const newClient = await createClient(enseigneCode, {
        clientId,
        displayName,
        data: initialPreferences ? { preferences: initialPreferences } : undefined,
      });
      return { ...newClient, clientId };
    },
    [getClient, createClient]
  );

  const getClientPreferences = useCallback(
    async (enseigneCode: string, clientId: string): Promise<ClientPreferences | null> => {
      const client = await getClient(enseigneCode, clientId);
      return client?.data?.preferences || null;
    },
    [getClient]
  );

  const updateClientPreferences = useCallback(
    async (
      enseigneCode: string,
      clientId: string,
      preferences: Partial<ClientPreferences>
    ): Promise<Client> => {
      let existingClient = await getClient(enseigneCode, clientId);
      
      if (!existingClient) {
        return await createClient(enseigneCode, {
          clientId,
          displayName: 'Utilisateur',
          data: { preferences: preferences as ClientPreferences },
        });
      }
      
      const updatedData = {
        ...existingClient?.data,
        preferences: {
          ...(existingClient?.data?.preferences || {}),
          ...preferences,
        },
      };

      return await updateClient(enseigneCode, clientId, {
        clientId,
        displayName: existingClient.displayName,
        data: updatedData,
      });
    },
    [getClient, createClient, updateClient]
  );

  const ensureClientExists = useCallback(
    async (
      enseigneCode: string,
      enseigneDisplayName: string,
      clientId: string,
      clientDisplayName: string,
      initialPreferences?: ClientPreferences
    ): Promise<Client> => {
      await getOrCreateEnseigne(enseigneCode, enseigneDisplayName);
      return await getOrCreateClient(enseigneCode, clientId, clientDisplayName, initialPreferences);
    },
    [getOrCreateEnseigne, getOrCreateClient]
  );

  const getClientLanguage = useCallback(
    async (enseigneCode: string, clientId: string): Promise<string | null> => {
      const preferences = await getClientPreferences(enseigneCode, clientId);
      return preferences?.language || null;
    },
    [getClientPreferences]
  );

  const updateClientLanguage = useCallback(
    async (enseigneCode: string, clientId: string, language: string): Promise<Client> => {
      return await updateClientPreferences(enseigneCode, clientId, { language });
    },
    [updateClientPreferences]
  );

  return {
    getClient,
    getClientPreferences,
    updateClientPreferences,
    getClientLanguage,
    updateClientLanguage,
    ensureClientExists,
  };
};
